import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Lock, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Suggestion", "Concern", "Complaint", "Commendation", "Other"];

export default function Feedback() {
  const [category, setCategory] = useState("Suggestion");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try { await api.post("/feedback", { category, message }); setDone(true); setMessage(""); toast.success("Submitted anonymously"); }
    catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader eyebrow="Your Voice, Protected" title="Anonymous Feedback"
        description="Share suggestions or concerns directly with the Officer Council. Your identity is never recorded." />

      <Card className="gold-ring glass p-6">
        <div className="mb-5 flex items-center gap-3 rounded-lg bg-[hsl(var(--water))]/10 p-4 text-sm text-[hsl(var(--water))]">
          <Lock className="h-5 w-5 shrink-0" />
          <p>This submission is <strong>fully anonymous</strong>. No user ID, name, or IP is stored with your message.</p>
        </div>

        {done ? (
          <div className="py-8 text-center" data-testid="feedback-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h3 className="mt-3 font-display text-2xl font-700">Received</h3>
            <p className="mt-1 text-sm text-muted-foreground">Thank you. The council will review your feedback.</p>
            <Button variant="outline" className="mt-5 gold-ring" onClick={() => setDone(false)} data-testid="feedback-another-button">Submit Another</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1.5" data-testid="feedback-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Speak freely…"
                className="mt-1.5 min-h-[140px]" data-testid="feedback-message-input" />
            </div>
            <Button type="submit" disabled={busy || !message.trim()} className="w-full bg-[hsl(var(--gold))] font-700 text-[#0A192F]" data-testid="feedback-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Submit Anonymously</>}
            </Button>
            <p className="flex items-center justify-center gap-1 text-center text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Encrypted channel to the Officer Council
            </p>
          </form>
        )}
      </Card>
    </div>
  );
}
